import { type RefObject, useEffect, useRef, useState } from 'react'

import { useDebounce } from './useDebounce'

export type DOMRectValues = Pick<
  DOMRectReadOnly,
  'bottom' | 'height' | 'left' | 'right' | 'top' | 'width'
> &
  Pick<ResizeObserverSize, 'blockSize' | 'inlineSize'>

/** Creates a MutationObserver over any Element, firing the Mutation callback function provided as an input param whenever
 * a change occurs in the DOM.
 */
export const useResizeObserver = <TElement extends HTMLElement | SVGElement>(
  elementRef: RefObject<TElement | null>,
  debounceTimeout = 100,
): DOMRectValues | null => {
  const observerRef = useRef<ResizeObserver | null>(null)

  const [DOMRect, setDOMRect] = useState<DOMRectValues>()
  const debouncedDOMRect = useDebounce(DOMRect, debounceTimeout)

  // creates the observer reference on mount
  useEffect(() => {
    const fn = (entries: ResizeObserverEntry[]) => {
      const entry = entries[0]
      const { bottom, height, left, right, top, width } = entry.contentRect
      const { blockSize, inlineSize } = entry.borderBoxSize[0]

      setDOMRect({
        bottom,
        height,
        left,
        right,
        top,
        width,
        blockSize,
        inlineSize,
      })
    }

    observerRef.current = new ResizeObserver(fn)

    return () => {
      observerRef?.current?.disconnect()
    }
  }, [debounceTimeout])

  // observes on the provided element ref
  useEffect(() => {
    if (elementRef?.current) {
      const boundingRect = elementRef.current.getBoundingClientRect()
      setDOMRect({ ...boundingRect, blockSize: 0, inlineSize: 0 })

      observerRef?.current?.observe(elementRef.current)
    }
  }, [elementRef])

  return debouncedDOMRect ?? DOMRect ?? null
}
